/**
 * CACApprovalGuard — route guard for the CAC approvals page.
 *
 * Access requires being a platform system admin OR holding the granular
 * role `cac_approver` (granted via the Platform Permissions admin API).
 * Unauthorized users get an access-denied screen instead of being
 * silently bounced to /settings (old SystemAdminGuard behaviour).
 */

import { Navigate } from 'react-router-dom';

import { useTenant } from '@/contexts/TenantContext';
import { useHasRole } from '@/hooks/useRoles';
import { usePlatformPermissions } from '@/hooks/usePlatformPermissions';

interface CACApprovalsGuardProps {
  children: React.ReactNode;
}

export const CACApprovalsGuard: React.FC<CACApprovalsGuardProps> = ({ children }) => {
  const { currentTenant, isLoading: tenantLoading } = useTenant();
  const isTenantAdmin = useHasRole('admin');
  const { roles, loading: rolesLoading } = usePlatformPermissions();

  if (tenantLoading || rolesLoading) {
    return null;
  }

  const isSystemAdmin = isTenantAdmin && currentTenant?.is_system_tenant === true;
  const isCACApprover = roles.includes('cac_approver');

  if (isSystemAdmin || isCACApprover) {
    return <>{children}</>;
  }

  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <h2 className="mb-2 text-lg font-semibold text-gray-800">Acceso denegado</h2>
      <p className="mb-6 max-w-md text-sm text-gray-500">
        El panel de aprobaciones del marketplace requiere el rol granular{' '}
        <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">cac_approver</code> otorgado por un
        administrador de plataforma.
      </p>
      <Navigate to="/marketplace" replace />
    </div>
  );
};